import React from "react";
import { format } from "date-fns";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";

pdfMake.vfs = pdfFonts.pdfMake.vfs;
const qz = require("qz-tray");
const DOCTOR_ASSESSMENT_HOSPITAL_NAME =
  "Sri Vedamaayu Ayurveda Hospital and Wellness Centre";
let doctorAssessmentLogoPromise;

// Loads the same logo used by the Patient Case Studies print header.
const getDoctorAssessmentLogo = () => {
  if (!doctorAssessmentLogoPromise) {
    const publicPath = process.env.PUBLIC_URL || "";

    doctorAssessmentLogoPromise = fetch(
      `${publicPath}/vedamaayu_small_logo.png`,
    )
      .then((response) => {
        if (!response.ok) {
          throw new Error("Unable to load Doctor Assessment logo.");
        }
        return response.blob();
      })
      .then(
        (blob) =>
          new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.onerror = () =>
              reject(new Error("Unable to convert Doctor Assessment logo."));
            reader.readAsDataURL(blob);
          }),
      );
  }

  return doctorAssessmentLogoPromise;
};

const value = (val) => (val === undefined || val === null || val === "" ? " " : String(val));

const patientName = (patientData) =>
  patientData?.fullName ||
  [patientData?.fname, patientData?.mname, patientData?.lname].filter(Boolean).join(" ") ||
  "";

const personName = (person) =>
  person?.fullName ||
  [
    person?.displaytitle || person?.title,
    person?.first_name || person?.firstName || person?.fname,
    person?.last_name || person?.lastName || person?.lname,
  ]
    .filter(Boolean)
    .join(" ")
    .trim();

const cell = (text, options = {}) => ({
  text: value(text),
  fontSize: options.fontSize || 9,
  bold: Boolean(options.bold),
  italics: Boolean(options.italics),
  decoration: options.decoration,
  alignment: options.alignment,
  margin: options.margin || [3, 3, 3, 3],
  border: options.border || [true, true, true, true],
  colSpan: options.colSpan,
  rowSpan: options.rowSpan,
  fillColor: options.fillColor,
});

const section = (title) => [
  cell(title, {
    bold: true,
    decoration: "underline",
    colSpan: 2,
    border: [false, false, false, false],
    margin: [3, 8, 3, 2],
  }),
  {},
];

const sanskritRow = (sanskrit, english, noteValue) => [
  {
    colSpan: 2,
    margin: [5, 5, 5, 8],
    stack: [
      { text: sanskrit, bold: true, decoration: "underline", fontSize: 9 },
      { text: `(${english})`, italics: true, color: "#444", fontSize: 8, margin: [0, 1, 0, 4] },
      { text: value(noteValue), fontSize: 9 },
    ],
  },
  {},
];

const simpleRow = (label, noteValue) => [
  {
    colSpan: 2,
    margin: [5, 5, 5, 8],
    stack: [
      { text: label, bold: true, fontSize: 9 },
      { text: value(noteValue), fontSize: 9, margin: [0, 4, 0, 0] },
    ],
  },
  {},
];

const yesDetailsRow = (label, answer, detailsLabel, detailsValue) => {
  const showDetails = String(answer || "").trim().toLowerCase() === "yes";

  return [
    {
      colSpan: 2,
      margin: [5, 5, 5, 8],
      stack: [
        {
          text: [
            { text: `${label}: `, bold: true, fontSize: 9 },
            { text: value(answer), fontSize: 9 },
          ],
        },
        ...(showDetails
          ? [
              {
                text: [
                  { text: `IF YES - ${detailsLabel}: `, bold: true, fontSize: 9 },
                  { text: value(detailsValue), fontSize: 9 },
                ],
                margin: [0, 4, 0, 0],
              },
            ]
          : []),
      ],
    },
    {},
  ];
};

const twoColRow = (label1, value1, label2, value2) => [
  {
    margin: [5, 5, 5, 5],
    text: [
      { text: `${label1}: `, bold: true, fontSize: 9 },
      { text: value(value1), fontSize: 9 },
    ],
  },
  {
    margin: [5, 5, 5, 5],
    text: [
      { text: `${label2}: `, bold: true, fontSize: 9 },
      { text: value(value2), fontSize: 9 },
    ],
  },
];

const printPdf = (docDefinition) => {
  pdfMake.createPdf(docDefinition).open();

  const printerName = process.env.REACT_APP_PRINTER_NAME;
  if (!printerName) return;

  pdfMake.createPdf(docDefinition).getBase64((data) => {
    qz.websocket
      .connect()
      .then(() => qz.printers.find())
      .then((printers) => {
        const printer = printers.find((p) => p.includes(printerName));
        if (!printer) throw new Error("Printer not found");
        const config = qz.configs.create(printer, {
          size: { width: 148, height: 210 },
          units: "mm",
        });
        return qz.print(config, [
          { type: "pixel", format: "pdf", flavor: "base64", data },
        ]);
      })
      .then(() => qz.websocket.disconnect())
      .catch(console.error);
  });
};

export async function openDoctorPrint(notes, patientData, printContext = {}) {
  const smallLogo = await getDoctorAssessmentLogo();
  const assessmentDate = printContext.assessmentDate
    ? format(new Date(printContext.assessmentDate), "dd-MM-yyyy")
    : format(new Date(), "dd-MM-yyyy");
  const isFemalePatient = ["f", "female"].includes(
    String(
      patientData?.sex ||
        patientData?.gender ||
        ""
    ).trim().toLowerCase()
  );
  const assignedDoctor =
    patientData?.visit?.person || patientData?.person || patientData?.doctor;
  const doctorName =
    printContext.doctorName ||
    patientData?.doctorName ||
    (typeof patientData?.doctor === "string" ? patientData.doctor : "") ||
    personName(assignedDoctor);
  const department =
    printContext.department ||
    patientData?.visit?.department ||
    patientData?.department ||
    assignedDoctor?.department;
  const ipOpNumber = patientData?.visit?.ipopnumber || patientData?.ipopnumber;
  const visitType = patientData?.visit?.type || patientData?.visitType || "";
  const ipOpLabel = /^OP/i.test(ipOpNumber || "") || /outpatient/i.test(visitType)
    ? "OP No."
    : "IP No.";
  const admissionDate =
    patientData?.visit?.admsnDate || patientData?.admsnDate;
  const doa = admissionDate
    ? format(new Date(admissionDate), "dd-MM-yyyy")
    : "";

  const docDefinition = {
    pageSize: "A4",
    pageMargins: [28, 80, 28, 24],
    header: () => ({
      margin: [40, 8, 40, 0],
      columns: [
        {
          image: smallLogo,
          fit: [55, 48],
          width: 60,
        },
        {
          width: "*",
          alignment: "center",
          margin: [0, 5, 0, 0],
          stack: [
            {
              text: DOCTOR_ASSESSMENT_HOSPITAL_NAME,
              color: "#176fad",
              bold: true,
              fontSize: 16,
              noWrap: true,
              margin: [0, 0, 0, 5],
            },
            {
              text: "DOCTOR'S INITIAL ASSESSMENT",
              bold: true,
              fontSize: 11,
            },
          ],
        },
        {
          width: 60,
          text: "",
        },
      ],
    }),
    content: [
      {
        table: {
          widths: ["40%", "30%", "30%"],
          body: [
            [
              cell(`Patient Name: ${patientName(patientData)}`, { bold: true }),
              cell(`Age/Sex: ${value(patientData?.age)}/${value(patientData?.sex || patientData?.gender)}`, { bold: true }),
              cell(`Main: ${value(patientData?.uhid || patientData?.id)}`, { bold: true }),
            ],
            [
              cell(`Date: ${assessmentDate}`, { bold: true }),
              cell(`DOA: ${doa}`, { bold: true }),
              cell(`${ipOpLabel}: ${value(ipOpNumber)}`, { bold: true }),
            ],
            [
              cell(`Name of the Doctor: ${value(doctorName)}`, { bold: true }),
              cell(`Department: ${value(department)}`, { bold: true, colSpan: 2 }),
              {},
            ],
          ],
        },
      },
      {
        table: {
          widths: ["50%", "50%"],
          body: [
            sanskritRow("PRADHANA VEDANA", "Chief Complaint", notes["Chief Complaint"]),
            sanskritRow("PRADHANA VEDANA VRITTANTA", "Present History", notes["Present History"]),
            sanskritRow("POORVA VYADHI VRITTANTA", "Past History", notes["Past History"]),
            sanskritRow("VAYAKTIKA VRITTANTA", "Personal History", notes["Personal History"]),
            ...(isFemalePatient
              ? [simpleRow("MENSTRUAL DETAILS", notes["Menstrual Details"])]
              : []),
            twoColRow("APPETITE", notes["Appetite"], "MICTURITION", notes["Micturition"]),
            twoColRow("BOWEL", notes["Bowel"], "SLEEP", notes["Sleep"]),
            simpleRow("FAMILY HISTORY", notes["Family History"]),
            section("General & Systemic Examination"),
            [
              {
                colSpan: 2,
                table: {
                  widths: ["20%", "20%", "20%", "20%", "20%"],
                  body: [
                    [
                      cell(`Temp\n${value(notes["Doctor Temp"])}`, { alignment: "center" }),
                      cell(`BP\n${value(notes["Doctor BP"])}`, { alignment: "center" }),
                      cell(`Pulse\n${value(notes["Doctor Pulse"])}`, { alignment: "center" }),
                      cell(`Height\n${value(notes["Doctor Height"])}`, { alignment: "center" }),
                      cell(`Weight\n${value(notes["Doctor Weight"])}`, { alignment: "center" }),
                    ],
                  ],
                },
                margin: [4, 4, 4, 4],
              },
              {},
            ],
            simpleRow("General & Systemic Examination Notes", notes["General Systemic Examination"]),
            yesDetailsRow(
              "ALLERGIES/ADVERSE REACTION",
              notes["Allergies Adverse Reaction"],
              "ALLERGY DETAILS",
              notes["Allergies Adverse Reaction Details"],
            ),
            yesDetailsRow(
              "VULNERABLE PATIENT",
              notes["Vulnerable Patient Doctor"],
              "VULNERABILITY DETAILS",
              notes["Vulnerable Patient Details"],
            ),
            simpleRow("LAB INVESTIGATION", notes["Lab Investigation"]),
            sanskritRow("CHIKITSA VRITTANTA", "Treatment history", notes["Treatment History"]),
            sanskritRow("VYADHI VINISHCHAYA", "Diagnosis", notes["Diagnosis"]),
            simpleRow("DOCTOR NOTES", notes["Doctor Notes"]),
            [
              {
                colSpan: 2,
                alignment: "left",
                margin: [5, 18, 5, 34],
                stack: [
                  { text: "Name & signature of the doctor :", bold: true, fontSize: 9 },
                  { text: "Date & time:", bold: true, fontSize: 9, margin: [0, 24, 0, 0] },
                ],
              },
              {},
            ],
          ],
        },
        margin: [0, 6, 0, 0],
      },
    ],
    defaultStyle: { fontSize: 9 },
  };

  printPdf(docDefinition);
}

export default function VedamaayuDoctorPrint({ notes, patientData }) {
  React.useEffect(() => {
    if (notes && patientData) {
      openDoctorPrint(notes, patientData);
    }
  }, [notes, patientData]);

  return null;
}
